/**
 * Issues List Hook
 *
 * React Query hook for fetching a paginated, filterable list of all issues
 * across all containers. Used by the dedicated Issues page.
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

// ============================================================================
// Types
// ============================================================================

export interface IssueListItem {
    issue_id: string;
    container_id: string;
    container_number: string | null;
    container_status: string | null;
    container_risk_score: number | null;
    title: string;
    description: string | null;
    issue_type: string;
    issue_source: string | null;
    severity: "critical" | "high" | "medium" | "low";
    resolution_status: string;
    blocks_shipment: boolean;
    root_cause: string | null;
    recommended_actions: string[] | null;
    resolution_notes: string | null;
    issue_metadata: Record<string, unknown> | null;
    created_at: string | null;
}

export interface IssuesListResponse {
    issues: IssueListItem[];
    total_count: number;
    skip: number;
    limit: number;
    has_more: boolean;
}

export interface IssuesListParams {
    severity?: string;
    status?: string;
    issue_type?: string;
    category?: string;
    blocks_shipment?: boolean;
    show_resolved?: boolean;
    search?: string;
    start_date?: string;
    end_date?: string;
    sort_by?: string;
    sort_order?: string;
    skip?: number;
    limit?: number;
}

// ============================================================================
// Query Keys
// ============================================================================

export const ISSUES_LIST_QUERY_KEYS = {
    all: ["issues-list"] as const,
    list: (params: IssuesListParams) =>
        [...ISSUES_LIST_QUERY_KEYS.all, params] as const,
};

// ============================================================================
// Hook
// ============================================================================

export function useIssuesList(params: IssuesListParams, enabled = true) {
    return useQuery({
        queryKey: ISSUES_LIST_QUERY_KEYS.list(params),
        queryFn: async (): Promise<IssuesListResponse> => {
            const queryParams: Record<string, string> = {};
            if (params.severity) queryParams.severity = params.severity;
            if (params.status) queryParams.status = params.status;
            if (params.issue_type) queryParams.issue_type = params.issue_type;
            if (params.category) queryParams.category = params.category;
            if (params.blocks_shipment !== undefined) queryParams.blocks_shipment = String(params.blocks_shipment);
            if (params.show_resolved !== undefined) queryParams.show_resolved = String(params.show_resolved);
            if (params.search) queryParams.search = params.search;
            if (params.start_date) queryParams.start_date = params.start_date;
            if (params.end_date) queryParams.end_date = params.end_date;
            if (params.sort_by) queryParams.sort_by = params.sort_by;
            if (params.sort_order) queryParams.sort_order = params.sort_order;
            if (params.skip !== undefined) queryParams.skip = String(params.skip);
            if (params.limit !== undefined) queryParams.limit = String(params.limit);

            return await apiGet<IssuesListResponse>(
                "/container-issues/issues",
                { params: queryParams },
            );
        },
        enabled,
        staleTime: 30_000,
        placeholderData: (previousData) => previousData,
    });
}
